import { memo, useState, useEffect, useCallback } from "react";
import { Accessibility, X, Sun, Moon, Type, AlignJustify, Link2, Check } from "lucide-react";

// Default accessibility settings
const DEFAULT_SETTINGS = {
  darkMode: false,
  fontSize: 'normal', // 'normal', 'large', 'extra-large'
  textSpacing: false,
  highlightLinks: false
};

// NOTE: Dark mode is SESSION ONLY - does not persist across page refreshes
// Load settings from sessionStorage (not localStorage) - resets on page refresh
const loadSettings = () => {
  // Always start with light mode (default settings)
  // Dark mode must be manually toggled each session
  return DEFAULT_SETTINGS;
};

// Save settings to sessionStorage (session only, not persistent)
const saveSettings = (settings) => {
  // Intentionally NOT saving to localStorage
  // Dark mode resets on every page refresh as per user requirement
};

// Apply settings to document
const applySettings = (settings) => {
  const root = document.documentElement;
  const body = document.body;
  
  // Dark Mode
  if (settings.darkMode) {
    root.classList.add('dark-mode');
    body.classList.add('dark-mode');
  } else {
    root.classList.remove('dark-mode');
    body.classList.remove('dark-mode');
  }
  
  // Font Size
  root.classList.remove('font-normal', 'font-large', 'font-extra-large');
  root.classList.add(`font-${settings.fontSize}`);
  
  // Text Spacing
  if (settings.textSpacing) {
    root.classList.add('text-spacing-wide');
  } else {
    root.classList.remove('text-spacing-wide');
  }
  
  // Highlight Links - DISABLED to prevent unwanted highlighting
  // Always remove highlight-links class
  root.classList.remove('highlight-links');
};

export const AccessibilityToolbar = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [mounted, setMounted] = useState(false);
  
  // Load settings on mount
  useEffect(() => {
    const loaded = loadSettings();
    setSettings(loaded);
    applySettings(loaded);
    setMounted(true);
  }, []);
  
  // Update setting
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      saveSettings(newSettings);
      applySettings(newSettings);
      return newSettings;
    });
  }, []);
  
  // Reset all settings
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    saveSettings(DEFAULT_SETTINGS);
    applySettings(DEFAULT_SETTINGS);
  }, []);
  
  // Toggle panel
  const togglePanel = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  
  if (!mounted) return null;
  
  // Check if dark mode is active
  const isDarkMode = settings.darkMode;
  
  return (
    <>
      {/* Floating Accessibility Button - Bottom Left - Match chatbot button style */}
      <button
        onClick={togglePanel}
        className={`fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-50 w-12 h-12 sm:w-16 sm:h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-emerald-800 text-white rotate-0' 
            : 'bg-emerald-900 text-white hover:bg-emerald-800 hover:scale-105 active:scale-95'
        }`}
        aria-label="Accessibility Options"
        aria-expanded={isOpen}
        data-testid="accessibility-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6 sm:w-7 sm:h-7" />
        ) : (
          <Accessibility className="w-6 h-6 sm:w-7 sm:h-7" />
        )}
      </button>
      
      {/* Accessibility Panel - Dark/Light mode aware */}
      {isOpen && (
        <div 
          className={`fixed bottom-20 sm:bottom-24 left-4 sm:left-6 z-[60] w-72 rounded-2xl shadow-2xl overflow-hidden ${
            isDarkMode 
              ? 'bg-slate-900 border border-slate-700' 
              : 'bg-white border border-slate-200'
          }`}
          role="dialog"
          aria-label="Accessibility Tools"
          data-testid="accessibility-panel"
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        >
          {/* Header */}
          <div className="bg-emerald-700 text-white px-4 py-3">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Accessibility className="w-4 h-4" />
              Accessibility Tools
            </h2>
            <p className="text-emerald-100 text-xs mt-0.5">Customize your viewing experience</p>
          </div>
          
          {/* Options */}
          <div className="p-3 space-y-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={() => updateSetting('darkMode', !settings.darkMode)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${
                settings.darkMode 
                  ? isDarkMode
                    ? 'bg-emerald-900/50 border-2 border-emerald-500'
                    : 'bg-emerald-100 border-2 border-emerald-500'
                  : isDarkMode
                    ? 'bg-slate-800 border-2 border-slate-600 hover:bg-slate-700'
                    : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
              }`}
              aria-pressed={settings.darkMode}
              data-testid="accessibility-dark-mode"
            >
              <div className="flex items-center gap-3">
                {settings.darkMode ? (
                  <Moon className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`} />
                ) : (
                  <Sun className={`w-5 h-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`} />
                )}
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                  Dark Mode
                </span>
              </div>
              {settings.darkMode && <Check className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />}
            </button>
            
            {/* Font Size */}
            <div className={`px-3 py-2.5 rounded-xl ${
              isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50'
            }`}>
              <div className="flex items-center gap-3 mb-2">
                <Type className={`w-5 h-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`} />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                  Font Size
                </span>
              </div>
              <div className="flex gap-1.5">
                {[
                  { id: 'normal', label: 'A', size: 'text-sm' },
                  { id: 'large', label: 'A', size: 'text-base' },
                  { id: 'extra-large', label: 'A', size: 'text-lg' }
                ].map((option) => (
                  <button
                    key={option.id}
                    onClick={() => updateSetting('fontSize', option.id)}
                    className={`flex-1 py-1.5 rounded-lg font-semibold transition-all ${option.size} ${
                      settings.fontSize === option.id
                        ? 'bg-emerald-600 text-white'
                        : isDarkMode
                          ? 'bg-slate-700 text-white hover:bg-slate-600 border border-slate-600'
                          : 'bg-white text-slate-600 hover:bg-emerald-50 border border-slate-200'
                    }`}
                    aria-pressed={settings.fontSize === option.id}
                    data-testid={`accessibility-font-${option.id}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Text Spacing */}
            <button
              onClick={() => updateSetting('textSpacing', !settings.textSpacing)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${
                settings.textSpacing 
                  ? isDarkMode
                    ? 'bg-emerald-900/50 border-2 border-emerald-500'
                    : 'bg-emerald-100 border-2 border-emerald-500'
                  : isDarkMode
                    ? 'bg-slate-800 border-2 border-slate-600 hover:bg-slate-700'
                    : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
              }`}
              aria-pressed={settings.textSpacing}
              data-testid="accessibility-text-spacing"
            >
              <div className="flex items-center gap-3">
                <AlignJustify className={`w-5 h-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`} />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                  Text Spacing
                </span>
              </div>
              {settings.textSpacing && <Check className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />}
            </button>
            
            {/* Highlight Links - Hidden since feature is disabled */}
            {/* 
            <button
              onClick={() => updateSetting('highlightLinks', !settings.highlightLinks)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${
                settings.highlightLinks 
                  ? isDarkMode
                    ? 'bg-emerald-900/50 border-2 border-emerald-500'
                    : 'bg-emerald-100 border-2 border-emerald-500'
                  : isDarkMode
                    ? 'bg-slate-800 border-2 border-slate-600 hover:bg-slate-700'
                    : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
              }`}
              aria-pressed={settings.highlightLinks}
              data-testid="accessibility-highlight-links"
            >
              <div className="flex items-center gap-3">
                <Link2 className={`w-5 h-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`} />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                  Highlight Links
                </span>
              </div>
              {settings.highlightLinks && <Check className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />}
            </button>
            */}
          </div>
          
          {/* Reset Button */}
          <div className="px-3 pb-3">
            <button
              onClick={resetSettings}
              className={`w-full py-2 text-xs rounded-lg transition-colors ${
                isDarkMode 
                  ? 'text-slate-400 hover:text-white hover:bg-slate-800' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
              data-testid="accessibility-reset"
            >
              Reset to Default
            </button>
          </div>
        </div>
      )}
    </>
  );
});

export default AccessibilityToolbar;
